/**
 * @file    useItemActionStates.ts
 * @scope   SHARED
 * @summary Memoized per-item and global control states for a list, driven by injected strategies.
 * @consumes Nothing domain-specific.
 * @produces useItemActionStates
 * @sideEffects None (pure memoization).
 * @notes Strategy pattern: each subtype supplies its own `computeItemState`/`computeGlobalState`.
 */
import { useMemo } from 'react';

export interface ItemActionStatesResult<TItemState, TGlobalState> {
  globalState: TGlobalState;
  /** Per-item state keyed by list index. */
  itemStates: Map<number, TItemState>;
}

/**
 * @param items List being rendered.
 * @param computeItemState Strategy computing one item's control state.
 * @param computeGlobalState Strategy computing list-level control state.
 * @param deps Extra inputs the strategies read (e.g. available options).
 * @returns Global state plus a per-index item state map.
 */
export const useItemActionStates = <TItem, TItemState, TGlobalState>(
  items: TItem[],
  computeItemState: (item: TItem, index: number, items: TItem[]) => TItemState,
  computeGlobalState: (items: TItem[]) => TGlobalState,
  deps: unknown[] = []
): ItemActionStatesResult<TItemState, TGlobalState> =>
  useMemo(() => {
    const itemStates = new Map<number, TItemState>();
    items.forEach((item, index) => itemStates.set(index, computeItemState(item, index, items)));
    return { globalState: computeGlobalState(items), itemStates };
    // WHY: strategies are expected to be stable/pure; callers declare their inputs via `deps`.
  }, [items, ...deps]);
